import React from "react";
import { Mail, Phone, MapPin, Clock, Shield, Printer, FileText } from "lucide-react";
import { motion } from "framer-motion";
import { COMPANY } from "@/data/company";

export default function Contact() {
  const lines = [
    {
      icon: Mail,
      label: "E-posta",
      value: COMPANY.email,
      href: `mailto:${COMPANY.email}`,
      testId: "contact-email",
    },
    {
      icon: Phone,
      label: "Çağrı Merkezi",
      value: COMPANY.phone,
      href: `tel:${COMPANY.phoneRaw}`,
      testId: "contact-phone",
    },
    {
      icon: Phone,
      label: "Sabit Hat",
      value: COMPANY.phoneLocal,
      href: `tel:${COMPANY.phoneLocalRaw}`,
      testId: "contact-phone-local",
    },
    {
      icon: Printer,
      label: "Faks",
      value: COMPANY.fax,
      testId: "contact-fax",
    },
    {
      icon: FileText,
      label: "KEP",
      value: COMPANY.kep,
      href: `mailto:${COMPANY.kep}`,
      testId: "contact-kep",
    },
    {
      icon: MapPin,
      label: "Adres",
      value: `${COMPANY.address}, ${COMPANY.district}`,
      testId: "contact-location",
    },
    {
      icon: Clock,
      label: "Çalışma Saatleri",
      value: "Hafta içi 09:00 — 18:00 · Acil Destek 7/24",
      testId: "contact-hours",
    },
  ];

  const mapsEmbed = `https://maps.google.com/maps?q=${encodeURIComponent(
    COMPANY.mapsQuery
  )}&t=&z=15&ie=UTF8&iwloc=&output=embed`;
  const mapsLink = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
    COMPANY.mapsQuery
  )}`;

  return (
    <div data-testid="page-contact" className="relative">
      <div className="absolute inset-x-0 top-0 h-[400px] bg-grid opacity-30 pointer-events-none" />
      <div
        className="absolute inset-x-0 top-0 h-[500px] pointer-events-none opacity-60"
        style={{
          background:
            "radial-gradient(ellipse at 70% 0%, rgba(255,90,0,0.2), transparent 60%)",
        }}
      />

      <section className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-16 pt-16 md:pt-24 pb-12">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-thermal-orange mb-4">
          / İletişim — Direct Channel
        </div>
        <h1 className="text-4xl md:text-6xl font-bold tracking-tight leading-[1.05] max-w-4xl">
          Tesisinizin termal güvenlik <span className="thermal-text">haritasını</span> birlikte çıkaralım.
        </h1>
        <p className="mt-6 max-w-2xl text-slate-300 text-lg leading-relaxed">
          Keşif görüşmesi, saha analizi ve özelleşmiş çözüm önerisi için iletişim kanallarımız:
        </p>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 pb-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Terminal-style contact */}
          <div className="lg:col-span-7 terminal-border p-7 md:p-10 relative overflow-hidden">
            <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-thermal-orange to-transparent" />

            <div className="flex items-center justify-between mb-7">
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-thermal-orange">
                / Command Terminal
              </div>
              <div className="flex gap-1.5">
                <span className="w-2 h-2 rounded-full bg-thermal-red" />
                <span className="w-2 h-2 rounded-full bg-thermal-yellow" />
                <span className="w-2 h-2 rounded-full bg-thermal-green animate-pulse" />
              </div>
            </div>

            <div className="font-mono text-xs text-slate-400 mb-7">
              <span className="text-thermal-green">root@zake</span>:<span className="text-thermal-orange">~</span>${" "}
              <span className="text-white">connect --target=zake.com.tr</span>
              <span className="ml-2 inline-block w-2 h-3 bg-thermal-orange animate-pulse align-middle" />
            </div>

            <div className="space-y-px bg-white/5 border border-white/10">
              {lines.map((l, i) => {
                const Icon = l.icon;
                const Inner = (
                  <>
                    <div className="w-10 h-10 border border-thermal-orange/40 flex items-center justify-center shrink-0 group-hover:bg-thermal-orange/10 transition-colors">
                      <Icon className="w-4 h-4 text-thermal-orange" strokeWidth={1.5} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                        / {l.label}
                      </div>
                      <div className="text-white font-semibold mt-0.5 break-all leading-snug">
                        {l.value}
                      </div>
                    </div>
                  </>
                );
                return (
                  <motion.div
                    key={l.testId}
                    initial={{ opacity: 0, x: -8 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.3, delay: i * 0.05 }}
                    data-testid={l.testId}
                    className="bg-[#06090F] group hover:bg-[#0D131F] transition-colors"
                  >
                    {l.href ? (
                      <a href={l.href} className="flex items-center gap-5 p-5">
                        {Inner}
                      </a>
                    ) : (
                      <div className="flex items-center gap-5 p-5">{Inner}</div>
                    )}
                  </motion.div>
                );
              })}
            </div>

            {/* Legal Info */}
            <div className="mt-6 border border-white/10 p-5">
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-3">
                / Yasal Bilgiler
              </div>
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                <div>
                  <dt className="text-slate-500 text-xs font-mono">Şirket Ünvanı</dt>
                  <dd className="text-slate-200 mt-0.5 leading-snug">
                    {COMPANY.fullName}
                  </dd>
                </div>
                <div>
                  <dt className="text-slate-500 text-xs font-mono">Vergi Dairesi</dt>
                  <dd className="text-slate-200 mt-0.5">{COMPANY.taxOffice}</dd>
                </div>
                <div>
                  <dt className="text-slate-500 text-xs font-mono">Sicil No</dt>
                  <dd className="text-slate-200 mt-0.5 font-mono">
                    {COMPANY.registrationNo}
                  </dd>
                </div>
                <div>
                  <dt className="text-slate-500 text-xs font-mono">MERSİS</dt>
                  <dd className="text-slate-200 mt-0.5 font-mono break-all">
                    {COMPANY.mersis}
                  </dd>
                </div>
              </dl>
            </div>

            <div className="mt-6 grid grid-cols-2 gap-px bg-white/5 border border-white/10">
              <div className="bg-[#06090F] p-5">
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                  / Response Time
                </div>
                <div className="font-mono text-2xl text-thermal-yellow mt-1">&lt; 24h</div>
              </div>
              <div className="bg-[#06090F] p-5">
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                  / Encryption
                </div>
                <div className="font-mono text-2xl text-thermal-green mt-1 flex items-center gap-2">
                  <Shield className="w-5 h-5" /> AES-256
                </div>
              </div>
            </div>
          </div>

          {/* Google Maps embed */}
          <div className="lg:col-span-5 relative terminal-border min-h-[420px] overflow-hidden flex flex-col">
            <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-thermal-orange to-transparent z-20" />
            <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 bg-[#06090F]/80 backdrop-blur-sm relative z-10">
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-thermal-orange">
                THRM://map-view · OSTİM
              </div>
              <a
                href={mapsLink}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="map-open"
                className="font-mono text-[10px] uppercase tracking-[0.2em] text-thermal-green hover:text-thermal-orange transition-colors"
              >
                Aç ↗
              </a>
            </div>
            <div className="relative flex-1 min-h-[380px] bg-[#0a0f18]">
              <iframe
                title="ZAKE Mühendislik Konum"
                src={mapsEmbed}
                className="absolute inset-0 w-full h-full grayscale contrast-125"
                style={{ border: 0, filter: "invert(0.92) hue-rotate(180deg) saturate(0.4)" }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                data-testid="map-iframe"
              />
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-thermal-orange/10 via-transparent to-thermal-red/10 mix-blend-overlay" />
              <div className="pointer-events-none absolute inset-0 scanline" />
              {/* Corner brackets */}
              <div className="pointer-events-none absolute top-2 left-2 w-4 h-4 border-l-2 border-t-2 border-thermal-orange" />
              <div className="pointer-events-none absolute top-2 right-2 w-4 h-4 border-r-2 border-t-2 border-thermal-orange" />
              <div className="pointer-events-none absolute bottom-2 left-2 w-4 h-4 border-l-2 border-b-2 border-thermal-orange" />
              <div className="pointer-events-none absolute bottom-2 right-2 w-4 h-4 border-r-2 border-b-2 border-thermal-orange" />
              <div
                data-testid="map-marker"
                className="pointer-events-none absolute bottom-3 left-3 font-mono text-[10px] uppercase tracking-[0.2em] text-thermal-orange bg-[#06090F]/85 border border-thermal-orange/30 px-2 py-1 backdrop-blur"
              >
                ZAKE — Uzayçağı Cad. 62/4
              </div>
              <div className="pointer-events-none absolute bottom-3 right-3 font-mono text-[10px] uppercase tracking-[0.2em] text-thermal-green bg-[#06090F]/85 px-2 py-1">
                ● Online
              </div>
            </div>
          </div>
        </div>

        {/* Bottom CTA cards */}
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-px bg-white/10 border border-white/10">
          {[
            { t: "Saha Keşfi", d: "Tesisinizde ücretsiz termal risk değerlendirmesi yapıyoruz." },
            { t: "Pilot Proje", d: "Kritik bölgenizde 30 günlük canlı demo kurulumu." },
            { t: "Eğitim & Destek", d: "Operasyon ekibiniz için tam eğitim ve 7/24 destek." },
          ].map((c, i) => (
            <div
              key={i}
              data-testid={`offer-${i}`}
              className="bg-[#06090F] p-7 hover:bg-[#0D131F] transition-colors"
            >
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-thermal-orange">
                / 0{i + 1}
              </div>
              <div className="mt-3 text-xl font-semibold">{c.t}</div>
              <div className="mt-2 text-sm text-slate-400 leading-relaxed">{c.d}</div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
